import { useState, useEffect } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LogoLockup, LogoPill } from "@/components/Logo";
import { Buildings, Check, User, EnvelopeSimple, LockKey, Phone, CloudSun, Package, Sparkle } from "@phosphor-icons/react";

const PLAN_META = {
  maintenance: {
    label: "Maintenance",
    icon: CloudSun,
    tagline: "Field crews & maintenance teams",
    perks: [
      "SweeperRadar™ weather-aware dispatch",
      "InspectRadar™ + AI work orders",
      "Before / after photo proof",
      "Assets + Snow & Lawn",
    ],
    accent: "#14b8a6",
  },
  operations: {
    label: "Operations",
    icon: Buildings,
    tagline: "Property management companies",
    perks: [
      "Everything in Maintenance",
      "Tenant + landlord portals",
      "Compliance, deposits, lease renewals",
      "Delinquency Radar + AI Voice Reception",
    ],
    accent: "#0891b2",
  },
  enterprise: {
    label: "Enterprise",
    icon: Package,
    tagline: "Multi-location & larger portfolios",
    perks: [
      "Everything in Operations",
      "Priority support",
      "Custom branding & integrations",
      "Dedicated onboarding",
    ],
    accent: "#7c3aed",
  },
};

const LOCKED_META = {
  pest_control: {
    name: "Pest Control",
    icon: Sparkle,
    accent: "#f97316",
    hero_kicker: "PEST CONTROL EDITION",
    hero_title: "ROUTE.\nSPRAY.\nREPORT.",
    tagline: "Ops center built for pest control crews — pesticide log, AI route optimizer, and photo-to-pest ID from your phone.",
    perks: [
      "EPA-ready pesticide application log",
      "AI Treatment Advisor + Photo-to-Pest ID",
      "Route Radar + AI Route Optimizer",
      "Branded service-ticket PDFs",
    ],
  },
  hvac: {
    name: "HVAC",
    icon: CloudSun,
    accent: "#0891b2",
    hero_kicker: "HVAC EDITION",
    hero_title: "DIAGNOSE.\nWARRANTY.\nCLOSE.",
    tagline: "Ops center built for HVAC crews — refrigerant log, warranty tracker, AI diagnostics, and load calc.",
    perks: [
      "EPA 608 refrigerant log",
      "Warranty & equipment radar",
      "AI Diagnose from nameplate photo",
      "Branded service-call PDFs",
    ],
  },
  roofing: {
    name: "Roofing",
    icon: Buildings,
    accent: "#dc2626",
    hero_kicker: "ROOFING EDITION",
    hero_title: "STORM.\nESTIMATE.\nCLOSE.",
    tagline: "Ops center built for roofers — weather-flagged jobs, AI damage diagnosis, and SMS estimates that close.",
    perks: [
      "NWS storm-flagged job radar",
      "AI Damage Diagnose from photos",
      "SMS estimates via Twilio",
      "Branded proposal PDFs",
    ],
  },
  home_health: {
    name: "Home Healthcare",
    icon: Package,
    accent: "#c026d3",
    hero_kicker: "HOME CARE EDITION",
    hero_title: "VISIT.\nDOCUMENT.\nESCALATE.",
    tagline: "Ops center built for home care agencies — vitals log, AI care advisor, photo journal, and case-manager PDFs.",
    perks: [
      "Vital signs log with flag alerts",
      "AI Care Plan Advisor",
      "Photo journal with Claude Vision",
      "Case-manager-ready care PDFs",
    ],
  },
  landlord: {
    name: "Landlord Autopilot",
    icon: Package,
    accent: "#0284c7",
    hero_kicker: "LANDLORD AUTOPILOT",
    hero_title: "COLLECT.\nDISPATCH.\nRELAX.",
    tagline: "Fire your PM company. Tenants pay rent via a magic link, AI triages every repair, and your vetted vendors get texted for you — even at 2 a.m.",
    perks: [
      "Magic-link renter portal — no app, no password",
      "AI triages repairs + auto-texts your vendors under your cap",
      "Stripe ACH + card rent collection (fee optionally passed to renter)",
      "Rent ledger + year-end 1099 export",
    ],
  },
  landscaping: {
    name: "Landscaping & Snow",
    icon: Package,
    accent: "#16a34a",
    hero_kicker: "LANDSCAPING & SNOW",
    hero_title: "ROUTE.\nSALT.\nBILL.",
    tagline: "Weather-triggered dispatch for snow crews + mow-and-blow route optimizer. Your salt bin knows when it's empty. Your invoices write themselves.",
    perks: [
      "Weather-triggered snow dispatch (NWS alerts → crew SMS)",
      "Route optimizer for mow-and-blow days",
      "Salt & fuel inventory with low-stock nudges",
      "Seasonal billing — spring cleanups, mid-season fert, snow pre-pays",
    ],
  },
  plumbing: {
    name: "Plumbing",
    icon: Package,
    accent: "#2563eb",
    hero_kicker: "PLUMBING EDITION",
    hero_title: "DIAGNOSE.\nQUOTE.\nCLOSE.",
    tagline: "Ops center built for plumbing crews — permit-ready job records, AI leak diagnostics from photos, and SMS estimates that turn kitchen-flood emergencies into paid invoices before you leave the driveway.",
    perks: [
      "AI Leak Diagnose from photo + water-meter reading",
      "Permit-ready work order PDFs (state code snippets attached)",
      "SMS estimates via Twilio — approved in one tap",
      "Truck-stock parts reorder + supplier price sync",
    ],
  },
  electrical: {
    name: "Electrical",
    icon: Package,
    accent: "#eab308",
    hero_kicker: "ELECTRICAL EDITION",
    hero_title: "PANEL.\nPERMIT.\nCLOSE.",
    tagline: "Ops center built for electricians — panel-schedule builder, AI load-calc from nameplate photos, and NEC-flagged findings that pass rough-in first time.",
    perks: [
      "AI Load-Calc from nameplate photo (Claude Vision)",
      "Panel schedule builder + printable label",
      "NEC-flagged findings on every inspection",
      "Permit + rough-in + final chain, all on one job record",
    ],
  },
  handyman: {
    name: "Handyman",
    icon: Package,
    accent: "#9333ea",
    hero_kicker: "HANDYMAN EDITION",
    hero_title: "BOOK.\nFIX.\nGET PAID.",
    tagline: "Ops center for solo handymen and 2-3 person crews. Booking Radar takes the calls, AI drafts the estimate, Stripe collects on-site. No PM software gymnastics.",
    perks: [
      "Booking Radar — customers self-book on your calendar",
      "AI estimate draft from the customer's description + photo",
      "Stripe tap-to-pay for card + Apple Pay on the truck",
      "Job photo journal → branded PDF invoice, one tap",
    ],
  },
  vendors: {
    name: "Maintenance Vendor",
    icon: Package,
    accent: "#0d9488",
    hero_kicker: "MAINTENANCE VENDOR",
    hero_title: "BID.\nWIN.\nBILL.",
    tagline: "Ops center for vendors who work for property managers — Bridge Inbox pulls PM work orders in, AI drafts your bid, and every completion photo lands right back on their portal.",
    perks: [
      "Bridge Inbox — one screen for every PM you serve",
      "AI Bid Drafter (Claude Haiku) — quote in 30 seconds",
      "Completion photos auto-sync to the PM's portal",
      "Per-PM SLA scorecard so you know who's worth keeping",
    ],
  },
};

export default function Signup({ lockedIndustry = null }) {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const planParam = (sp.get("plan") || "").toLowerCase();
  const initialPlan = PLAN_META[planParam] ? planParam : "";
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  // Industry picker — pre-selects from ?wedge=hvac|roofing|... on landing pages
  const wedgeParam = (sp.get("wedge") || "").toLowerCase();
  const [industries, setIndustries] = useState([]);
  const [industry, setIndustry] = useState(
    lockedIndustry
      ? lockedIndustry
      : ["pm","hvac","roofing","plumbing","electrical","home_health","handyman","vendors","pest_control","landlord","landscaping"].includes(wedgeParam)
        ? wedgeParam
        : ""
  );
  useEffect(() => {
    api.get("/public/industries").then(r => setIndustries(r.data.industries || [])).catch(() => {});
  }, []);
  const lockMeta = lockedIndustry ? LOCKED_META[lockedIndustry] : null;
  const [f, setF] = useState({ company_name: "", admin_name: "", admin_email: "", admin_password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  // Persist selection across bounces (login → signup, etc.)
  useEffect(() => {
    if (selectedPlan) {
      try { localStorage.setItem("fr_selected_plan", selectedPlan); } catch { /* noop */ }
    } else {
      try {
        const stored = localStorage.getItem("fr_selected_plan");
        if (stored && PLAN_META[stored]) setSelectedPlan(stored);
      } catch { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user) return <Navigate to={user.platform_role === "super_admin" ? "/super-admin" : user.role === "admin" ? "/admin" : "/worker"} replace />;

  const changePlan = (p) => {
    setSelectedPlan(p);
    const next = new URLSearchParams(sp);
    if (p) next.set("plan", p); else next.delete("plan");
    setSp(next, { replace: true });
    if (p) { try { localStorage.setItem("fr_selected_plan", p); } catch { /* noop */ } }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_name || !f.admin_name || !f.admin_email || !f.admin_password) {
      toast.error("All required fields must be filled");
      return;
    }
    if (f.admin_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!industry) {
      toast.error("Pick an industry so we can tailor your workspace");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/tenants/signup", {
        company_name: f.company_name.trim(),
        admin_name: f.admin_name.trim(),
        admin_email: f.admin_email.trim().toLowerCase(),
        admin_password: f.admin_password,
        phone: f.phone.trim(),
        industry,
        industry_locked: !!lockedIndustry,
        selected_plan: selectedPlan || "",
      });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      if (selectedPlan) {
        try { localStorage.setItem("fr_selected_plan", selectedPlan); } catch { /* noop */ }
      }
      setUser(data.user);
      toast.success(`Welcome to Field Radar, ${data.user.name}!`);
      // Locked verticals need a fresh IndustryContext (industry + labels + modules).
      // A soft navigate keeps the old context in memory — hard-reload guarantees
      // the sidebar renders the right vertical group on first paint.
      if (lockedIndustry) {
        window.location.href = "/admin";
      } else {
        nav("/admin");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  const plan = selectedPlan ? PLAN_META[selectedPlan] : null;
  const PlanIcon = plan?.icon;

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-5">
      {/* left brand pane */}
      <div className="hidden lg:flex lg:col-span-3 relative bg-[#0f172a] text-white p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxIVkFDJTIwdGVjaG5pY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzg1MDczMTE2fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: "cover", backgroundPosition: "center"
        }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] via-[#0f172a]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <LogoPill height={44}/>
          </div>
          <div>
            <div className="mb-3 inline-flex items-center gap-2 px-3 py-1 bg-[#00C4CC]/20 border border-[#00C4CC] text-[#00C4CC] text-[10px] font-black uppercase tracking-widest" data-testid="signup-hero-kicker">
              {lockMeta ? lockMeta.hero_kicker : "FIELD RADAR"}
            </div>
            <h1 className="font-display font-black text-6xl leading-[0.95] tracking-tight whitespace-pre-line">
              {lockMeta ? lockMeta.hero_title : "START YOUR\nCREW.\nFREE FOREVER."}
            </h1>
            <p className="mt-6 text-lg max-w-lg text-slate-300 font-medium">
              {lockMeta ? lockMeta.tagline : "Spin up your own Field Radar workspace in under 60 seconds. Add your logo, invite your team, start dispatching."}
            </p>
            <ul className="mt-8 space-y-3 text-slate-200 font-medium">
              {(lockMeta ? lockMeta.perks : [
                "1 worker on the Free plan — upgrade anytime",
                "AI diagnostics + receipt/nameplate scanners",
                "Branded PDF work orders with your logo",
                "Your data is 100% isolated from other companies",
              ]).map((p, i) => (
                <li key={i} className="flex items-center gap-2"><Check weight="bold" className="text-[#00C4CC]" size={20}/> {p}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* right form pane */}
      <div className="col-span-1 lg:col-span-2 flex items-center justify-center p-6 sm:p-10 bg-[#f4f4f5] overflow-y-auto">
        <div className="w-full max-w-md py-6">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <LogoLockup height={44}/>
          </div>

          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight" data-testid="signup-heading">
            {lockMeta ? `Start Your ${lockMeta.name} Crew` : "Create Your Company"}
          </h2>
          <p className="mt-2 text-slate-500 font-medium">You'll be the admin. Add your crew later.</p>

          {!lockedIndustry && plan && PlanIcon && (
            <div className="mt-5 border-2 border-[#1f2937] bg-white p-4 relative" data-testid="plan-preselect-banner">
              <div className="absolute -top-2 left-3 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-white"
                   style={{ backgroundColor: plan.accent }}>
                Your Selection
              </div>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 flex items-center justify-center border-2 border-slate-200"
                     style={{ backgroundColor: `${plan.accent}22`, color: plan.accent }}>
                  <PlanIcon weight="fill" size={22}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <Sparkle weight="fill" size={10}/> {plan.tagline}
                  </div>
                  <div className="font-display font-black text-xl tracking-tight text-slate-900" data-testid="plan-label">{plan.label} plan</div>
                  <ul className="mt-2 space-y-0.5">
                    {plan.perks.map((p, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[11px] text-slate-600 leading-snug">
                        <Check weight="bold" size={12} style={{ color: plan.accent }} className="mt-0.5 flex-shrink-0"/>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <button type="button" onClick={() => changePlan("")}
                    data-testid="plan-change-btn"
                    className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 underline">
                    Change plan
                  </button>
                </div>
              </div>
            </div>
          )}

          {!lockedIndustry && !plan && (
            <div className="mt-5" data-testid="plan-picker">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Pick a plan (you can change later)</div>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(PLAN_META).map(([key, m]) => {
                  const I = m.icon;
                  return (
                    <button key={key} type="button" onClick={() => changePlan(key)}
                      data-testid={`plan-pick-${key}`}
                      className="p-3 border-2 border-slate-200 hover:border-slate-900 bg-white text-left transition">
                      <I weight="fill" size={18} style={{ color: m.accent }}/>
                      <div className="mt-1 font-black text-sm tracking-tight">{m.label}</div>
                      <div className="text-[9px] text-slate-500 leading-tight">{m.tagline}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="signup-form">
            {/* Industry picker — tailors sidebar + terminology on day 1 */}
            {lockedIndustry ? (
              <div data-testid="signup-industry-locked" className="border-2 border-[#00C4CC] bg-[#00C4CC]/5 p-4 relative">
                <div className="absolute -top-2 left-3 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-white bg-[#00C4CC]">
                  This workspace
                </div>
                <div className="flex items-center gap-3">
                  {lockMeta?.icon && (() => { const I = lockMeta.icon; return <I weight="fill" size={26} style={{ color: lockMeta.accent }}/>; })()}
                  <div>
                    <div className="font-display font-black text-lg tracking-tight text-slate-900">
                      {lockMeta?.name || industry}
                    </div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Locked to this vertical — one-product workspace
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div data-testid="signup-industry-picker">
                <label className="text-sm font-bold uppercase tracking-wide flex items-center gap-1.5">
                  <Buildings size={16}/> What kind of business?
                </label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(industries.length ? industries : [
                    {key:"pm",name:"Property Mgmt"},{key:"hvac",name:"HVAC"},
                    {key:"roofing",name:"Roofing"},{key:"plumbing",name:"Plumbing"},
                    {key:"electrical",name:"Electrical"},{key:"handyman",name:"Handyman"},
                    {key:"home_health",name:"Home Health"},{key:"vendors",name:"Maint. Vendor"},
                  ]).map(i => (
                    <button key={i.key} type="button" onClick={() => setIndustry(i.key)}
                      data-testid={`signup-industry-${i.key}`}
                      className={`min-h-[52px] px-2 py-2 border-2 text-center transition flex items-center justify-center ${industry === i.key
                        ? "border-[#00C4CC] bg-[#00C4CC]/10"
                        : "border-slate-200 hover:border-slate-900 bg-white"}`}>
                      <div className={`text-[11px] sm:text-xs font-black uppercase leading-tight break-words ${industry === i.key ? "text-[#00C4CC]" : "text-slate-800"}`}>
                        {i.name}
                      </div>
                    </button>
                  ))}
                </div>
                {industry && (
                  <div className="mt-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-600">
                    ✓ Selected — your sidebar + terminology will match {(industries.find(i => i.key === industry) || {}).name || industry}
                  </div>
                )}
              </div>
            )}
            <Field label="Company Name" icon={<Buildings size={16}/>}
              value={f.company_name} onChange={v => setF({ ...f, company_name: v })}
              placeholder="Acme Property Services" testid="signup-company"/>
            <Field label="Your Name" icon={<User size={16}/>}
              value={f.admin_name} onChange={v => setF({ ...f, admin_name: v })}
              placeholder="Jane Smith" testid="signup-name"/>
            <Field label="Email" type="email" icon={<EnvelopeSimple size={16}/>}
              value={f.admin_email} onChange={v => setF({ ...f, admin_email: v })}
              placeholder="jane@acmeprops.com" testid="signup-email"/>
            <Field label="Password" type="password" icon={<LockKey size={16}/>}
              value={f.admin_password} onChange={v => setF({ ...f, admin_password: v })}
              placeholder="At least 6 characters" testid="signup-password"/>
            <Field label="Phone (optional)" icon={<Phone size={16}/>}
              value={f.phone} onChange={v => setF({ ...f, phone: v })}
              placeholder="555-1234" testid="signup-phone"/>

            <button
              type="submit"
              disabled={busy}
              className="tactile-btn w-full h-16 bg-[#00C4CC] hover:bg-[#00a8af] text-white font-display font-black text-lg uppercase tracking-wide disabled:opacity-60"
              data-testid="signup-submit-btn"
            >
              {busy ? "Creating your workspace..." : selectedPlan ? `Start ${PLAN_META[selectedPlan].label} — Free 7 Days` : "Start Free"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500 font-medium">
            Already have an account?{" "}
            <Link to="/login" className="font-black uppercase tracking-wider text-[#00C4CC] underline" data-testid="signup-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, icon }) {
  return (
    <div>
      <label className="text-sm font-bold uppercase tracking-wide flex items-center gap-1.5">
        {icon}{label}
      </label>
      <input
        type={type}
        required={!label.includes("optional")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full h-12 px-4 bg-white border-2 border-[#1f2937] focus:outline-none focus:ring-0 focus:border-[#00C4CC] font-medium"
        data-testid={testid}
      />
    </div>
  );
}
